import {
  createSlice,
  createAsyncThunk,
  SerializedError,
} from '@reduxjs/toolkit';
import { LOCATION_CHANGE } from 'connected-react-router';
import { RegisterOptions } from 'react-hook-form';

import { RootState, AppThunkConfig } from '../../app/store';
import { RoleBinding, UserRoleBinding, isUserRoleBinding, TenantRoleBinding, isTenantRoleBinding } from '../../types';
import { bindAPI } from '../../app/auth';
import {
  ListRoleBindingsParams,
  ListRoleBindingsResponse,
  listRoleBindings,
  CreateRoleBindingParams,
  CreateRoleBindingResponse,
  createRoleBinding,
  DeleteRoleBindingParams,
  DeleteRoleBindingResponse,
  deleteRoleBinding,
} from '../../handlers';
import {
  logoutAction,
} from '../auth/authSlice';
import {
  setCurrentTenantId,
} from '../user/userSlice';

export type RoleState = {
  role_bindings: RoleBinding[];
  list_status: 'idle'|'loading'|'failed'|'success';
  error?: SerializedError;
}

const initialState: RoleState = {
  role_bindings: [],
  list_status: 'idle',
};

export type ListRoleBindingsActionParams = ListRoleBindingsParams;
export type ListRoleBindingsActionResponse = ListRoleBindingsResponse;
export const listRoleBindingsAction = createAsyncThunk<
  ListRoleBindingsActionResponse, ListRoleBindingsActionParams, AppThunkConfig
>(
  'role_binding/list',
  async(params, thunkAPI) => {
    return bindAPI(listRoleBindings, thunkAPI)(params);
  }
);

export type CreateRoleBindingActionParams = CreateRoleBindingParams;
export type CreateRoleBindingActionResponse = CreateRoleBindingResponse;
export const createRoleBindingAction = createAsyncThunk<
  CreateRoleBindingActionResponse, CreateRoleBindingParams, AppThunkConfig
>(
  'role_binding/create',
  (params, thunkAPI) => {
    return bindAPI(createRoleBinding, thunkAPI)(params);
  }
);

export type DeleteRoleBindingActionParams = DeleteRoleBindingParams;
export type DeleteRoleBindingActionResponse = DeleteRoleBindingResponse;
export const deleteRoleBindingAction = createAsyncThunk<
  DeleteRoleBindingActionResponse, DeleteRoleBindingParams, AppThunkConfig
>(
  'role_binding/delete',
  (params, thunkAPI) => {
    return bindAPI(deleteRoleBinding, thunkAPI)(params);
  }
);

export const roleBindingSlice = createSlice({
  name: 'role',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(listRoleBindingsAction.pending, (state) => {
        state.list_status = 'loading';
      })
      .addCase(listRoleBindingsAction.fulfilled, (state, action) => {
        state.list_status = 'success';
        state.role_bindings = action.payload.data;
      })
      .addCase(listRoleBindingsAction.rejected, (state, action) => {
        state.list_status = 'failed';
        state.error = action.error;
      })
      .addCase(logoutAction, (state) => initialState)
      .addCase(setCurrentTenantId, (state) => initialState)
      .addCase(LOCATION_CHANGE, (state) => initialState);
  },
});

export const selectListRoleBindingsAPIStatus = (state: RootState) => state.role_binding.list_status;
export const selectRoleBindings = (state: RootState) => state.role_binding.role_bindings;
export const selectUserRoleBindings = (state: RootState): UserRoleBinding[] => {
  return state.role_binding.role_bindings.filter(isUserRoleBinding);
};
export const selectTenantRoleBindings = (state: RootState): TenantRoleBinding[] => {
  return state.role_binding.role_bindings.filter(isTenantRoleBinding);
};

export const userRoleBindingFieldOptions: Partial<Record<keyof UserRoleBinding, RegisterOptions>> = {
  role_id: {
    required: 'Required',
  },
  user_id: {
    required: 'Required',
  },
};

export const tenantRoleBindingFieldOptions: Partial<Record<keyof TenantRoleBinding, RegisterOptions>> = {
  role_id: {
    required: 'Required',
  },
  shared_tenant_id: {
    required: 'Required',
  },
};
